import { useState, useEffect } from 'react';

/**
 * useDelayed
 *
 * Delays the start of an animation by returning a boolean that flips to `true`
 * after the specified delay (in milliseconds). Pair with `AnimatedWrapper`'s
 * `isAnimationStarted` prop to stagger or delay animations.
 *
 * @param delay - Time in milliseconds to wait before the animation starts.
 * @returns `true` once the delay has elapsed, `false` until then.
 *
 * @example
 * ```tsx
 * const isReady = useDelayed(500);
 *
 * <AnimatedWrapper animation={fadeIn} isAnimationStarted={isReady}>
 *   <Card />
 * </AnimatedWrapper>
 * ```
 */
export const useDelayed = (delay: number): boolean => {
  const [isReady, setIsReady] = useState(delay <= 0);

  useEffect(() => {
    if (delay <= 0) {
      setIsReady(true);
      return undefined;
    }

    setIsReady(false);

    const timer = setTimeout(() => {
      setIsReady(true);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [delay]);

  return isReady;
};
